import { z } from 'zod'
import { responseSchema } from '@/api/contract'
import type { components } from '@/api/openapi.gen'

/**
 * Códigos gerados no cliente. Códigos vindos do backend (envelope `error.code`)
 * são preservados como string e não precisam estar nesta lista.
 */
export type ClientErrorCode =
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'INVALID_RESPONSE'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'VALIDATION_ERROR'
  | 'SERVER_ERROR'
  | 'UNKNOWN'

/** Envelope único de erro do backend v2: {"error": {code, message, request_id, details?}}. */
export const errorEnvelopeSchema = responseSchema<components['schemas']['ErrorEnvelope']>()(
  z.object({
    error: z.object({
      code: z.string(),
      message: z.string(),
      request_id: z.string().nullish(),
      details: z.unknown().optional(),
    }),
  }),
)

export type ErrorKind =
  | 'network'
  | 'timeout'
  | 'invalid_response'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'validation'
  | 'server'
  | 'unknown'

export class ApiError extends Error {
  readonly code: string
  readonly status: number | null
  readonly requestId: string | null
  readonly details: unknown

  constructor(
    code: string,
    message: string,
    options: { status?: number | null; requestId?: string | null; details?: unknown } = {},
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = options.status ?? null
    this.requestId = options.requestId ?? null
    this.details = options.details
  }

  /** Classificação estável para a UI, independente do código específico do backend. */
  get kind(): ErrorKind {
    if (this.code === 'NETWORK_ERROR') return 'network'
    if (this.code === 'TIMEOUT') return 'timeout'
    if (this.code === 'INVALID_RESPONSE') return 'invalid_response'
    switch (this.status) {
      case 401:
        return 'unauthorized'
      case 403:
        return 'forbidden'
      case 404:
        return 'not_found'
      case 409:
        return 'conflict'
      case 400:
      case 422:
        return 'validation'
      default:
        if (this.status !== null && this.status >= 500) return 'server'
        return 'unknown'
    }
  }

  /** Erros que não adianta repetir automaticamente. */
  get retryable(): boolean {
    return ['network', 'timeout', 'server', 'unknown'].includes(this.kind)
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

export function codeForStatus(status: number): ClientErrorCode {
  if (status === 401) return 'UNAUTHORIZED'
  if (status === 403) return 'FORBIDDEN'
  if (status === 404) return 'NOT_FOUND'
  if (status === 409) return 'CONFLICT'
  if (status === 400 || status === 422) return 'VALIDATION_ERROR'
  if (status >= 500) return 'SERVER_ERROR'
  return 'UNKNOWN'
}
